import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ImagePlus, Video, Youtube, MapPin, Trash2, ShieldCheck, AlertTriangle, Upload } from 'lucide-react';
import { toast } from 'sonner';

const MAX_PHOTO = 5 * 1024 * 1024;
const MAX_VIDEO = 10 * 1024 * 1024;

function youtubeId(url = '') {
  const m = url.match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([\w-]+)/);
  return m ? m[1] : null;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result.split(',')[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export function PropertyMedia({ propertyId, canManage }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [contentCache, setContentCache] = useState({});
  const [photoCaption, setPhotoCaption] = useState('');
  const [videoCaption, setVideoCaption] = useState('');
  const [yt, setYt] = useState({ url: '', caption: '' });
  const [loc, setLoc] = useState({ maps_url: '', lat: '', lng: '', caption: '' });

  const load = async () => {
    const { data } = await api.get(`/properties/${propertyId}/media`);
    setItems(data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (propertyId) load(); }, [propertyId]);

  const photos = items.filter(i => i.kind === 'photo');
  const videos = items.filter(i => i.kind === 'video');
  const youtubes = items.filter(i => i.kind === 'youtube');
  const location = items.find(i => i.kind === 'location');

  const fetchContent = async (m) => {
    if (contentCache[m.id]) return contentCache[m.id];
    const { data } = await api.get(`/properties/${propertyId}/media/${m.id}/content`);
    setContentCache((c) => ({ ...c, [m.id]: data.content_base64 }));
    return data.content_base64;
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) return toast.error('Use JPEG, PNG or WebP');
    if (file.size > MAX_PHOTO) return toast.error(`Photo too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB`);
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      await api.post(`/properties/${propertyId}/media`, { kind: 'photo', content_base64: b64, mime_type: file.type, caption: photoCaption || null });
      toast.success('Photo uploaded'); setPhotoCaption(''); e.target.value = ''; load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
      e.target.value = '';
    } finally { setBusy(false); }
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith('video/')) return toast.error('Please choose a video file');
    if (file.size > MAX_VIDEO) return toast.error(`Video too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 10 MB`);
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      await api.post(`/properties/${propertyId}/media`, { kind: 'video', content_base64: b64, mime_type: file.type, caption: videoCaption || null });
      toast.success('Video uploaded'); setVideoCaption(''); e.target.value = ''; load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
      e.target.value = '';
    } finally { setBusy(false); }
  };

  const addYoutube = async () => {
    if (!yt.url) return toast.error('Paste a YouTube URL');
    setBusy(true);
    try {
      await api.post(`/properties/${propertyId}/media`, { kind: 'youtube', url: yt.url, caption: yt.caption || null });
      toast.success('YouTube link added'); setYt({ url: '', caption: '' }); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const setLocation = async () => {
    const payload = { kind: 'location', caption: loc.caption || null };
    if (loc.maps_url) payload.maps_url = loc.maps_url;
    if (loc.lat && loc.lng) { payload.lat = Number(loc.lat); payload.lng = Number(loc.lng); }
    if (!payload.maps_url && payload.lat === undefined) return toast.error('Provide a Maps URL or lat/lng');
    setBusy(true);
    try {
      await api.post(`/properties/${propertyId}/media`, payload);
      toast.success('Location saved'); setLoc({ maps_url: '', lat: '', lng: '', caption: '' }); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm('Remove this media?')) return;
    await api.delete(`/properties/${propertyId}/media/${m.id}`);
    toast.success('Removed'); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
        <ShieldCheck className="h-3.5 w-3.5 text-[hsl(152_60%_34%)]" />
        All uploads are screened for abusive, political, illegal or unauthorized content. Limits: 5 photos · 2 videos · 2 YouTube links · 1 location.
      </div>

      <Tabs defaultValue="photos">
        <TabsList>
          <TabsTrigger value="photos" data-testid="media-tab-photos"><ImagePlus className="h-3.5 w-3.5 mr-1" /> Photos ({photos.length}/5)</TabsTrigger>
          <TabsTrigger value="videos" data-testid="media-tab-videos"><Video className="h-3.5 w-3.5 mr-1" /> Videos ({videos.length}/2)</TabsTrigger>
          <TabsTrigger value="youtube" data-testid="media-tab-youtube"><Youtube className="h-3.5 w-3.5 mr-1" /> YouTube ({youtubes.length}/2)</TabsTrigger>
          <TabsTrigger value="location" data-testid="media-tab-location"><MapPin className="h-3.5 w-3.5 mr-1" /> Location</TabsTrigger>
        </TabsList>

        <TabsContent value="photos" className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((p) => <PhotoTile key={p.id} m={p} canManage={canManage} onRemove={remove} fetchContent={fetchContent} />)}
            {!photos.length && <div className="col-span-full text-sm text-muted-foreground text-center py-6">No photos yet.</div>}
          </div>
          {canManage && photos.length < 5 && (
            <Card className="p-3 border-dashed">
              <div className="space-y-2">
                <Label className="text-xs">Caption (optional)</Label>
                <Input value={photoCaption} onChange={(e) => setPhotoCaption(e.target.value)} placeholder="e.g. Living room with city view" data-testid="photo-caption-input" />
                <label className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 w-fit">
                  <Upload className="h-3.5 w-3.5" /> {busy ? 'Uploading…' : 'Choose photo (max 5 MB, JPEG/PNG/WebP)'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadPhoto} disabled={busy} data-testid="photo-file-input" />
                </label>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="videos" className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {videos.map((v) => <VideoTile key={v.id} m={v} canManage={canManage} onRemove={remove} fetchContent={fetchContent} propertyId={propertyId} />)}
            {!videos.length && <div className="col-span-full text-sm text-muted-foreground text-center py-6">No videos yet.</div>}
          </div>
          {canManage && videos.length < 2 && (
            <Card className="p-3 border-dashed">
              <div className="space-y-2">
                <Label className="text-xs">Caption (optional)</Label>
                <Input value={videoCaption} onChange={(e) => setVideoCaption(e.target.value)} placeholder="e.g. Walkthrough of unit A-101" />
                <label className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 w-fit">
                  <Upload className="h-3.5 w-3.5" /> {busy ? 'Uploading…' : 'Choose video (max 10 MB)'}
                  <input type="file" accept="video/*" className="hidden" onChange={uploadVideo} disabled={busy} data-testid="video-file-input" />
                </label>
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="youtube" className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {youtubes.map((y) => {
              const id = youtubeId(y.url);
              return (
                <Card key={y.id} className="overflow-hidden">
                  {id ? (
                    <div className="aspect-video bg-black">
                      <iframe src={`https://www.youtube.com/embed/${id}`} className="w-full h-full" allowFullScreen title="YouTube"></iframe>
                    </div>
                  ) : <div className="aspect-video bg-muted grid place-items-center text-muted-foreground"><Youtube /></div>}
                  <div className="p-3 flex items-start justify-between gap-2">
                    <div className="text-xs">{y.caption || y.url}</div>
                    {canManage && <Button size="sm" variant="outline" onClick={() => remove(y)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </div>
                </Card>
              );
            })}
            {!youtubes.length && <div className="col-span-full text-sm text-muted-foreground text-center py-6">No YouTube links yet.</div>}
          </div>
          {canManage && youtubes.length < 2 && (
            <Card className="p-3 border-dashed space-y-2">
              <Label className="text-xs">YouTube URL</Label>
              <Input value={yt.url} onChange={(e) => setYt({ ...yt, url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" data-testid="youtube-url-input" />
              <Label className="text-xs">Caption (optional)</Label>
              <Input value={yt.caption} onChange={(e) => setYt({ ...yt, caption: e.target.value })} placeholder="What does this video show?" />
              <Button size="sm" onClick={addYoutube} disabled={busy} data-testid="youtube-add-button">Add YouTube link</Button>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="location" className="space-y-3">
          {location ? (
            <Card className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium"><MapPin className="h-4 w-4" /> {location.caption || 'Property location'}</div>
                  {location.maps_url && <a href={location.maps_url} target="_blank" rel="noreferrer" className="text-xs text-[hsl(var(--role-accent))] underline mt-1 block">Open in Google Maps</a>}
                  {location.lat != null && <div className="text-xs text-muted-foreground mt-1 font-mono">{location.lat}, {location.lng}</div>}
                </div>
                {canManage && <Button size="sm" variant="outline" onClick={() => remove(location)}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            </Card>
          ) : <div className="text-sm text-muted-foreground text-center py-6">No location set.</div>}
          {canManage && (
            <Card className="p-3 border-dashed space-y-2">
              <Label className="text-xs">Google Maps link</Label>
              <Input value={loc.maps_url} onChange={(e) => setLoc({ ...loc, maps_url: e.target.value })} placeholder="https://maps.google.com/?q=…" data-testid="maps-url-input" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Latitude</Label>
                  <Input value={loc.lat} onChange={(e) => setLoc({ ...loc, lat: e.target.value })} placeholder="19.0700" data-testid="lat-input" />
                </div>
                <div>
                  <Label className="text-xs">Longitude</Label>
                  <Input value={loc.lng} onChange={(e) => setLoc({ ...loc, lng: e.target.value })} placeholder="72.8400" data-testid="lng-input" />
                </div>
              </div>
              <Label className="text-xs">Caption (optional)</Label>
              <Input value={loc.caption} onChange={(e) => setLoc({ ...loc, caption: e.target.value })} placeholder="e.g. Main gate, Khar West" />
              <Button size="sm" onClick={setLocation} disabled={busy} data-testid="location-save-button">{location ? 'Replace location' : 'Save location'}</Button>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PhotoTile({ m, canManage, onRemove, fetchContent }) {
  const [src, setSrc] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchContent(m).then((b64) => setSrc(`data:${m.mime_type};base64,${b64}`)); }, [m.id]);
  return (
    <div className="relative group rounded-lg overflow-hidden border">
      {src ? <img src={src} alt={m.caption || ''} className="w-full h-32 object-cover" /> : <div className="w-full h-32 bg-muted animate-pulse" />}
      {m.caption && <div className="text-xs px-2 py-1 bg-card border-t truncate">{m.caption}</div>}
      {canManage && (
        <button onClick={() => onRemove(m)} className="absolute top-1 right-1 bg-card/95 backdrop-blur rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </button>
      )}
    </div>
  );
}

function VideoTile({ m, canManage, onRemove, fetchContent, propertyId }) {
  const [src, setSrc] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchContent(m).then((b64) => setSrc(`data:${m.mime_type};base64,${b64}`)); }, [m.id]);
  return (
    <Card className="overflow-hidden">
      {src ? <video src={src} controls className="w-full aspect-video bg-black object-contain" /> : <div className="w-full aspect-video bg-muted animate-pulse" />}
      <div className="p-3 flex items-start justify-between gap-2">
        <div className="text-xs">{m.caption || '—'}</div>
        {canManage && <Button size="sm" variant="outline" onClick={() => onRemove(m)}><Trash2 className="h-3.5 w-3.5" /></Button>}
      </div>
    </Card>
  );
}
